import { useCallback, useEffect, useState } from "react";
import { Bell, BellOff, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { ensurePushRegistration, onAppResume, openAppNotificationSettings, requestPushPermission } from "../lib/capacitor";
import { needsPermissionRecheck } from "../lib/notificationPermission";
import { ensureWebPushSubscription, getWebPushState } from "../lib/webPush";

/**
 * Makes sure the console the user is standing in can actually receive notifications, and says so when it can't.
 *
 * Why this exists: the only places that ever asked for notification permission were the customer onboarding
 * screen and the customer/rider profile cards — all of which live in the customer shell. The merchant and admin
 * consoles render outside it (App.js returns early for /admin, /merchant and /rider), so a shop owner or admin
 * using the app was never asked on Android 13+, never got an FCM token, and never received a push. Orders still
 * appeared on screen because Live Orders polls, which is why this looked like "works in the foreground only".
 *
 * On native it picks up the registration boot had to defer and offers the OS prompt. In a browser it registers
 * the web-push service worker and subscribes, so a minimised window still gets a real notification. Renders
 * nothing at all once notifications are working, and never blocks the page.
 */
export default function NotificationGate() {
    const [state, setState] = useState(null);   // "granted" | "denied" | "prompt" | "unsupported" | ...
    const [native, setNative] = useState(false);
    const [busy, setBusy] = useState(false);

    const sync = useCallback(async () => {
        const push = await ensurePushRegistration();
        if (push.state !== "web") {
            setNative(true);
            // Permission granted but the backend never accepted the token: the device is unreachable while
            // looking perfectly healthy. Surface it rather than rendering nothing.
            setState(push.state === "granted" && push.registered === false ? "unregistered" : push.state);
            return;
        }
        setNative(false);
        const web = await getWebPushState();
        if (web.state === "granted") {
            // Already allowed — finish the subscription silently (no prompt, so no user gesture needed).
            const r = await ensureWebPushSubscription();
            setState(r.state);
            return;
        }
        setState(web.state);
    }, []);

    useEffect(() => { sync(); }, [sync]);

    // Back from Settings with notifications switched on: pick it up now, not after the app is restarted — the same
    // re-check CustomerHeader does for location. Only while this warning is on screen, so a healthy device makes no
    // extra calls. Native-only: onAppResume is a no-op in a browser.
    useEffect(() => {
        if (!needsPermissionRecheck(state)) return undefined;
        let unsubscribe = () => {};
        let unmounted = false;
        onAppResume(() => { sync(); }).then((off) => { if (unmounted) off(); else unsubscribe = off; });
        return () => { unmounted = true; unsubscribe(); };
    }, [state, sync]);

    const enable = async () => {
        setBusy(true);
        try {
            if (native && state === "unregistered") {
                // Permission is already granted — this only needs the registration to succeed.
                const r = await ensurePushRegistration();
                setState(r.registered ? "granted" : "unregistered");
                if (r.registered) toast.success("This device is registered for order notifications.");
                else toast.error("Couldn't register this device. Check your connection and try again.");
            } else if (native) {
                const r = await requestPushPermission();
                if (r.state === "granted") {
                    await ensurePushRegistration();
                    toast.success("Order notifications are on for this device.");
                } else if (r.state === "denied") {
                    toast.warning("Notifications were blocked earlier — opening system settings.", { duration: 6000 });
                    await openAppNotificationSettings();
                }
                setState(r.state);
            } else {
                const r = await ensureWebPushSubscription({ prompt: true });
                if (r.state === "granted") toast.success("Browser notifications are on for this device.");
                else if (r.state === "denied") toast.warning("Notifications are blocked for this site. Allow them in your browser's site settings.", { duration: 8000 });
                else if (r.state === "unconfigured") toast.info("Browser notifications aren't set up on this platform yet. Ask your Super Admin to add Web Push keys.", { duration: 8000 });
                setState(r.state);
            }
        } finally {
            setBusy(false);
        }
    };

    // Working, or nothing we can do about it here — say nothing.
    if (!state || state === "granted" || state === "unsupported" || state === "web" || state === "error") return null;
    // Configuration gap, not a user decision: only the Super Admin can fix it, so don't nag the admin every load.
    if (state === "unconfigured") return null;

    const blocked = state === "denied";
    const unregistered = state === "unregistered";
    return (
        <div
            className="mb-3 rounded-xl border border-amber-300 bg-amber-50 text-amber-900 px-3 py-2.5 flex items-center gap-3"
            role="status"
            data-testid="notification-gate"
        >
            {blocked ? <BellOff size={18} className="shrink-0" /> : <Bell size={18} className="shrink-0 animate-pulse" />}
            <div className="flex-1 min-w-0">
                <p className="text-sm font-bold leading-tight">
                    {blocked ? "Order notifications are blocked"
                        : unregistered ? "This device isn't registered for notifications"
                            : "Turn on order notifications"}
                </p>
                <p className="text-xs leading-snug mt-0.5">
                    {blocked
                        ? "This device won't alert you about new orders until notifications are allowed again."
                        : unregistered
                            ? "Notifications are allowed, but this device couldn't register with the server — so new orders won't reach it while the app is closed."
                            : "Without this you'll only see new orders while this screen is open."}
                </p>
            </div>
            <button
                type="button"
                onClick={enable}
                disabled={busy}
                data-testid="notification-gate-enable"
                className="touch-target shrink-0 rounded-lg bg-amber-500 text-white text-xs font-bold px-3 py-2 disabled:opacity-60 inline-flex items-center gap-1.5"
            >
                {busy && <Loader2 size={14} className="animate-spin" />}
                {blocked ? "Fix in settings" : unregistered ? "Retry" : "Enable"}
            </button>
        </div>
    );
}
